import type { TFunction } from 'i18next';
import { DefaultScores } from '../../constants';
import type { PsychologicalResult } from '../../model';
import formatScoreOutOf from '../formatters';
import { organisationalAiReadinessHeight } from '.';
import type { AssessmentResultItemDataFragment } from '@/gql/graphql';

const parseOrganisationalAiReadiness = (
    t: TFunction,
    items: AssessmentResultItemDataFragment[]
): PsychologicalResult | null => {
    const item = items.find(i => i.result.__typename === 'OrganisationalAiReadinessResult');
    if (!item || item.result.__typename !== 'OrganisationalAiReadinessResult') {
        return null;
    }

    const { moduleInstance } = item;
    const oarResult = item.result;

    if (!moduleInstance?.moduleDefinitionIdentifier) {
        return null;
    }

    const categories = oarResult?.categoryAverages || [];
    if (categories.length === 0) {
        return null;
    }

    const configLevels =
        moduleInstance.currentConfiguration?.__typename === 'OrganisationalAiReadinessModuleInstanceConfiguration'
            ? moduleInstance.currentConfiguration.levels
            : [];

    const formatLevelLabel = (name: string) => {
        const idx = configLevels.findIndex(l => l.name === name);
        return idx >= 0 ? t('traineeReports:organisationalAiReadiness.levelLabel', { number: idx + 1, name }) : name;
    };

    const radarData = categories.map(c => ({
        axis: c.category,
        individual: Number(c.averageScore.toFixed(1)),
    }));

    const hasValidMax = categories.some(c => (c.maximumPossibleScore ?? 0) > 0);
    const maxDomain = hasValidMax
        ? Math.max(1, ...categories.map(c => c.maximumPossibleScore ?? 0))
        : DefaultScores.OrganisationalAiReadinessMaxScore;

    const overallScore = oarResult?.overallScore;

    const scoreCards = [
        {
            label: t('traineeReports:organisationalAiReadiness.score'),
            value: overallScore
                ? formatScoreOutOf(t, overallScore.score, overallScore.maximumPossibleScore, 1)
                : t('traineeReports:na'),
        },
        {
            label: t('traineeReports:organisationalAiReadiness.maturityLevel'),
            value: overallScore?.label ? formatLevelLabel(overallScore.label) : t('traineeReports:na'),
        },
    ];

    const dimensionBreakdown = categories.map(c => ({
        category: c.category,
        label: c.label,
        score: formatScoreOutOf(t, c.averageScore, c.maximumPossibleScore, 1),
        description: c.description ?? undefined,
        badgeColor: c.badgeColor,
    }));

    return {
        moduleDefinitionIdentifier: moduleInstance.moduleDefinitionIdentifier,
        name: moduleInstance.name ?? '',
        description: moduleInstance.description ?? '',
        radarGroups: [
            {
                key: 'organisational-ai-readiness',
                scoreCards,
                charts: [
                    {
                        data: radarData,
                        maxDomain,
                        height: organisationalAiReadinessHeight,
                        variant: 'bare',
                    },
                ],
                dimensionBreakdown,
            },
        ],
    };
};

export default parseOrganisationalAiReadiness;
